import { useMemo } from 'react';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { openProject, ProviderErrorResponse, type UnifiedProject } from '@apis/projects';
import { writeLastSiteId } from '@utils/lastSite';
import { pickSite } from '@utils/pickSite';
import { useProjectsList } from '@hooks/useProjectsList';
import { Menu } from '@components/ui/Menu/Menu';
import { setWorkingSiteSuccess } from '@store/slices/site.slice';
import { Building2Icon } from '@utils/icons';
import styles from './styles.module.css';

export function ProjectSelector() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const match = useMatch('/monitor/:siteId');
  const routeSiteId = match?.params.siteId ?? null;

  const { data: projects = [], isLoading, error } = useProjectsList();

  const active = useMemo(() => pickSite(projects, routeSiteId), [projects, routeSiteId]);

  const handleSelect = async (project: UnifiedProject) => {
    try {
      // joinKey === siteId; openProject resolves the Site by id (resolve-only).
      const workingSite = await openProject(project.joinKey);
      dispatch(setWorkingSiteSuccess({ workingSite }));
      writeLastSiteId(workingSite.siteId);
      navigate(`/monitor/${encodeURIComponent(workingSite.siteId)}`);
    } catch (err) {
      if (err instanceof ProviderErrorResponse) {
        navigate(`/connect/${err.provider}`);
        return;
      }
      // Surface unexpected failures so the click isn't silently lost. A toast
      // system would be nicer; until we add one, console + alert covers the
      // rare network / upstream-5xx case.
      console.error('openProject failed', err);
      window.alert(t('topbar.openFailed'));
    }
  };

  if (error instanceof ProviderErrorResponse) {
    return (
      <button
        type="button"
        className={styles.reconnect}
        onClick={() => navigate(`/connect/${error.provider}`)}
      >
        {t('topbar.reconnectProvider', { provider: error.provider })}
      </button>
    );
  }
  if (isLoading && !active) {
    return (
      <span className={styles.placeholder} aria-busy="true">
        {t('topbar.selectProject')}
      </span>
    );
  }
  // App starts empty: no synced sites means there's nothing to switch to, so
  // the selector becomes a shortcut into the Sync / New Site page.
  if (projects.length === 0) {
    return (
      <Link to="/admin/project-merge" className={styles.syncLink}>
        <span className={styles.buildingIcon}><Building2Icon size={17} /></span>
        {t('topbar.noSitesSync')}
      </Link>
    );
  }

  return (
    <Menu.Root>
      <Menu.Trigger className={styles.trigger} aria-label={t('topbar.switchProject')}>
        <span className={styles.buildingIcon}><Building2Icon size={17} /></span>
        <span className={styles.projectName}>
          {active?.name ?? t('topbar.selectProject')}
        </span>
        <svg
          className={styles.chev}
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={6} align="start">
          <Menu.Popup>
            {projects.map((project) => (
              <Menu.Item
                key={project.joinKey}
                onClick={() => void handleSelect(project)}
                data-active={project.joinKey === active?.joinKey || undefined}
              >
                {project.name}
              </Menu.Item>
            ))}
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}
